import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Typography,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from '@mui/material';
import { useHistory } from 'react-router';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import useAuth from 'src/context/AuthContext';
import ROUTE from 'src/utils/routeConstants';
import FMSModal from 'src/common/FMSModal';
import { USER_ROLES } from 'src/utils/constants';

const AccountPopover = (props) => {
  const { anchorEl, onClose, open } = props;
  const history = useHistory();
  const { getUser } = useAuth();
  const user = getUser();
  const isOperator = user?.role === USER_ROLES.operator;
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  const handleSignOut = () => setShowAlert(true);

  const gotoAccountPage = () => {
    history.push(ROUTE.profile);
    onClose();
  };

  const gotoSettingsPage = () => {
    history.push(ROUTE.settings);
    onClose();
  };

  return (
    <>
      <LogoutModal open={showAlert} onClose={handleAlertClose} />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }}
        onClose={onClose}
        open={open}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 220, sm: 240 },
              borderRadius: 3,
              border: '1px solid #E5ECF6',
              boxShadow: '0 18px 42px rgba(15, 35, 75, 0.16)',
              overflow: 'hidden',
              bgcolor: '#FFFFFF'
            }
          }
        }}
      >
        <Box sx={{ pt: 2, pb: 1.5, px: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#10213D', fontWeight: 800 }}>
            {`${user?.first_name} ${user?.last_name}`}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B', textTransform: 'capitalize' }}>
            {user?.role}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: '#EEF2F8' }} />
        <MenuList
          disablePadding
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1,
            '& > *': {
              borderRadius: 2
            }
          }}
        >
          {!isOperator && (
            <MenuItem
              sx={{
                minHeight: 46,
                px: 1.25,
                py: 1,
                gap: 1,
                color: '#334155',
                transition: 'background-color 160ms ease, color 160ms ease',
                '&:hover': {
                  bgcolor: '#EFF6FF',
                  color: '#2563EB',
                  '& .MuiListItemIcon-root': { color: '#2563EB' }
                }
              }}
              onClick={gotoAccountPage}
            >
              <ListItemIcon sx={{ minWidth: 30, color: '#64748B' }}>
                <ManageAccountsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Account" primaryTypographyProps={{ fontWeight: 650 }} />
            </MenuItem>
          )}
          {!isOperator && (
            <MenuItem
              sx={{
                minHeight: 46,
                px: 1.25,
                py: 1,
                gap: 1,
                color: '#334155',
                transition: 'background-color 160ms ease, color 160ms ease',
                '&:hover': {
                  bgcolor: '#EFF6FF',
                  color: '#2563EB',
                  '& .MuiListItemIcon-root': { color: '#2563EB' }
                }
              }}
              onClick={gotoSettingsPage}
            >
              <ListItemIcon sx={{ minWidth: 30, color: '#64748B' }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 650 }} />
            </MenuItem>
          )}
          <MenuItem
            sx={{
              minHeight: 46,
              px: 1.25,
              py: 1,
              gap: 1,
              color: '#DC2626',
              transition: 'background-color 160ms ease, color 160ms ease',
              '&:hover': {
                bgcolor: '#FFF1F2',
                color: '#B91C1C',
                '& .MuiListItemIcon-root': { color: '#B91C1C' }
              }
            }}
            onClick={handleSignOut}
          >
            <ListItemIcon sx={{ minWidth: 30, color: '#DC2626' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign out" primaryTypographyProps={{ fontWeight: 650 }} />
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired
};

export default AccountPopover;

export const LogoutModal = ({ open, onClose }) => {
  const { signout } = useAuth();

  return (
    <FMSModal
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={onClose}
      PaperProps={{
        sx: {
          m: { xs: 2, sm: 3 },
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
        Confirm Logout
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ textAlign: 'center' }}>
          Are you sure you want to end your session?
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} sx={{ minWidth: 100 }}>
          Cancel
        </Button>
        <Button
          className="logout-confirm-button"
          variant="contained"
          color="error"
          onClick={signout}
          sx={{
            minWidth: 100,
            backgroundColor: '#DC2626 !important',
            color: '#FFFFFF !important',
            '&:hover': { backgroundColor: '#B91C1C !important' }
          }}
        >
          Logout
        </Button>
      </DialogActions>
    </FMSModal>
  );
};

LogoutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
